import {ReactNode, useEffect, useState} from 'react';
import {fetchUserAttributes, signOut} from 'aws-amplify/auth';
import {useAuth} from '../hooks/useAuth';
import {UserAttributesContext} from '../context/UserAttributesContext';

// Fetches Cognito user attributes once a session is established.
// AppLayout consumes the result to decide UserLayout vs PublicLayout.
// Mirrors flexi-bot-app/src/providers/UserAttributesProvider.tsx.
//
// On `NotAuthorizedException` with an inactive token, signs out and hard-
// reloads to `/` — the same self-healing pattern bot/portal use.

export const UserAttributesProvider = ({children}: {children?: ReactNode}) => {
    const [givenName, setGivenName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [tier, setTier] = useState('');
    const [accountId, setAccountId] = useState('');

    const {user} = useAuth();

    useEffect(() => {
        if (!user) return;

        (async () => {
            try {
                const fetched = await fetchUserAttributes();
                if (fetched.given_name) setGivenName(fetched.given_name);
                if (fetched.family_name) setFamilyName(fetched.family_name);
                if (fetched.name) setName(fetched.name);
                if (fetched.email) setEmail(fetched.email);
                if (fetched['custom:company_name']) setCompanyName(fetched['custom:company_name']);
                if (fetched['custom:tier']) setTier(fetched['custom:tier']);
                // ⚠️ custom:account_id, NOT `sub`. UserAccountProvider keys
                // console-api's getAccount on this value.
                if (fetched['custom:account_id']) setAccountId(fetched['custom:account_id']);
            } catch (e: unknown) {
                if (e instanceof Error && e.name === 'NotAuthorizedException' && e.message.includes('Token is inactive')) {
                    await signOut();
                    window.location.href = '/';
                }
            }
        })();
    }, [user]);

    if (!user) return <>{children}</>;

    return (
        <UserAttributesContext.Provider
            value={{givenName, familyName, name, email, companyName, tier, accountId}}
        >
            {children}
        </UserAttributesContext.Provider>
    );
};
